import { useEffect, useMemo, useRef, useState } from "react";
import { Check, X, ChevronDown } from "lucide-react";

interface FieldRuleInlineEditorProps {
  /** Anchor box in image natural pixels. */
  anchor: { x: number; y: number; width: number; height: number };
  /** Image natural width/height — used to decide placement. */
  imageNatural: { width: number; height: number };
  initialValue: string;
  /** Options can be strings or {value, label} objects */
  options: string[] | Array<{ value: string; label: string }>;
  onConfirm: (value: string) => void;
  onCancel: () => void;
  /** Called when user types and confirms a brand-new value not in options */
  onAddNew?: (newValue: string) => void;
}

/**
 * Inline editor for Field Rules, anchored to the drawn bounding box.
 */
export function FieldRuleInlineEditor({
  anchor,
  imageNatural,
  initialValue,
  options,
  onConfirm,
  onCancel,
  onAddNew,
}: FieldRuleInlineEditorProps) {
  // Combobox model: a single text input doubles as the selected-value
  // display AND the type-to-filter search. `value` is the committed
  // selection (what gets sent to onConfirm). `query` is whatever the
  // user is typing. When the input has focus we filter against
  // `query`; when it blurs we reset `query` so the input shows the
  // committed value again.
  const [value, setValue] = useState(initialValue);
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  // Dismiss the editor when the user clicks anywhere outside the
  // popover. Uses `mousedown` (not `click`) so the cancel fires before
  // any focus / drawing handlers on the document image swallow the
  // event. The popover's own onClick already stops propagation, so
  // interactions inside (dropdown, OK/CANCEL) won't trigger this.
  useEffect(() => {
    const handlePointerDown = (e: MouseEvent) => {
      const node = popoverRef.current;
      if (!node) return;
      if (e.target instanceof Node && !node.contains(e.target)) {
        onCancel();
      }
    };
    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [onCancel]);

  const normalizedOptions = useMemo(() => {
    return options.map(opt => 
      typeof opt === "string" ? { value: opt, label: opt } : opt
    );
  }, [options]);

  const filteredOptions = useMemo(() => {
    const q = query.toLowerCase().trim();
    if (!q) return normalizedOptions;
    return normalizedOptions.filter(opt =>
      opt.label.toLowerCase().includes(q) || opt.value.toLowerCase().includes(q)
    );
  }, [normalizedOptions, query]);

  // Show "Use: [typed]" when query doesn't match any existing option.
  const showCustomOption = useMemo(() => {
    const trimmed = query.trim();
    if (!trimmed) return false;
    return !normalizedOptions.some(opt => opt.value.toLowerCase() === trimmed.toLowerCase());
  }, [query, normalizedOptions]);

  const commitValue = (next: string) => {
    setValue(next);
    setQuery("");
    setIsOpen(false);
    inputRef.current?.blur();
  };

  const handleSelectCustom = () => {
    const trimmed = query.trim();
    if (!trimmed) return;
    onAddNew?.(trimmed);
    commitValue(trimmed);
  };

  // What the input shows: while the user is typing, show the live
  // query; otherwise show the committed selection's label so the
  // field doesn't appear blank between edits.
  const selectedLabel =
    normalizedOptions.find((o) => o.value === value)?.label ?? value;
  const inputDisplay = isOpen ? query : selectedLabel;

  const naturalW = Math.max(imageNatural.width, 1);
  const naturalH = Math.max(imageNatural.height, 1);

  // Positioning logic similar to FieldEditPopover
  const popoverW = 320;
  const popoverH = 180;
  const gap = 15;

  let left = (anchor.x / naturalW) * 100;
  let top = ((anchor.y + anchor.height) / naturalH) * 100;
  let transform = "translate(0, 10px)";

  const tooCloseToBottom = (anchor.y + anchor.height + popoverH + gap) > naturalH;
  if (tooCloseToBottom) {
    top = (anchor.y / naturalH) * 100;
    transform = "translate(0, calc(-100% - 10px))";
  }

  const tooCloseToRight = (anchor.x + popoverW) > naturalW;
  if (tooCloseToRight) {
    left = ((anchor.x + anchor.width) / naturalW) * 100;
    transform = tooCloseToBottom ? "translate(-100%, calc(-100% - 10px))" : "translate(-100%, 10px)";
  }  return (
    <div
      ref={popoverRef}
      className="absolute z-[100] flex w-auto min-w-[280px] items-center gap-1.5 rounded-lg border border-gray-300 bg-white/95 p-1.5 shadow-2xl backdrop-blur-sm"
      style={{ left: `${left}%`, top: `${top}%`, transform }}
      onClick={e => e.stopPropagation()}
      onMouseDown={e => e.stopPropagation()}
    >
      <div className="flex-1">
        {/* Combobox — single input that doubles as the selected-value
            display and the type-to-filter search. Replaces the older
            two-step "click to open → type in nested search input"
            flow. Pressing Enter commits the top match (or the typed
            value as a custom field); Escape cancels typing without
            closing the popover. */}
        <div className="relative">
          <div
            className={`flex items-center justify-between gap-1 rounded border bg-white px-2 py-1 transition-colors h-7 ${
              isOpen
                ? "border-blue-500 ring-1 ring-blue-200"
                : "border-gray-200 hover:bg-gray-50"
            }`}
          >
            <input
              ref={inputRef}
              type="text"
              value={inputDisplay}
              placeholder="Select field..."
              onFocus={() => setIsOpen(true)}
              onChange={(e) => {
                setQuery(e.target.value);
                if (!isOpen) setIsOpen(true);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  if (filteredOptions.length > 0) {
                    commitValue(filteredOptions[0].value);
                  } else if (showCustomOption) {
                    handleSelectCustom();
                  }
                } else if (e.key === "Escape") {
                  e.preventDefault();
                  setQuery("");
                  setIsOpen(false);
                  inputRef.current?.blur();
                } else if (e.key === "ArrowDown" && !isOpen) {
                  setIsOpen(true);
                }
              }}
              onBlur={() => {
                // Defer so a click inside the dropdown lands before
                // we close it.
                setTimeout(() => {
                  setIsOpen(false);
                  setQuery("");
                }, 120);
              }}
              className="flex-1 truncate bg-transparent text-[11px] font-medium text-gray-900 placeholder:text-gray-400 focus:outline-none"
            />
            <button
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                setIsOpen((v) => !v);
                if (!isOpen) inputRef.current?.focus();
              }}
              className="text-gray-400 hover:text-gray-700"
              aria-label="Toggle field list"
            >
              <ChevronDown
                className={`h-3 w-3 transition-transform ${isOpen ? "rotate-180" : ""}`}
              />
            </button>
          </div>

          {isOpen && (
            <div className="absolute left-0 bottom-full mb-1 w-full rounded border border-gray-200 bg-white shadow-xl z-[110]">
              <ul className="max-h-40 overflow-y-auto py-0.5">
                {showCustomOption && (
                  <li>
                    <button
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={handleSelectCustom}
                      className="flex w-full items-center gap-1.5 px-2 py-1.5 text-left text-[11px] text-blue-700 font-semibold hover:bg-blue-50 transition-colors border-b border-gray-100"
                    >
                      <span className="text-gray-400">Use:</span> "{query.trim()}"
                    </button>
                  </li>
                )}
                {filteredOptions.length === 0 && !showCustomOption ? (
                  <li className="px-2 py-1.5 text-[10px] text-gray-500">No results</li>
                ) : (
                  filteredOptions.map((opt) => (
                    <li key={opt.value}>
                      <button
                        type="button"
                        // Prevent the input's onBlur from firing
                        // before this click runs (which would close
                        // the dropdown and swallow the selection).
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => commitValue(opt.value)}
                        className={`flex w-full items-center justify-between px-2 py-1.5 text-left text-[11px] hover:bg-blue-50 transition-colors ${
                          value === opt.value ? 'bg-blue-50 text-blue-700 font-semibold' : 'text-gray-700'
                        }`}
                      >
                        {opt.label}
                        {value === opt.value && <Check className="h-3 w-3 text-blue-600" />}
                      </button>
                    </li>
                  ))
                )}
              </ul>
            </div>
          )}
        </div>
      </div>

      <div className="flex gap-1">
        <button
          type="button"
          onClick={() => onConfirm(value)}
          disabled={!value}
          className="flex h-7 items-center justify-center gap-1 rounded bg-[#007bff] px-3 text-[10px] font-bold text-white shadow-sm transition-colors hover:bg-blue-700 disabled:opacity-50"
        >
          <Check className="h-3 w-3" />
          OK
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="flex h-7 items-center justify-center gap-1 rounded bg-white px-3 text-[10px] font-bold text-gray-700 shadow-sm border border-gray-300 transition-colors hover:bg-gray-50"
        >
          <X className="h-3 w-3" />
          CANCEL
        </button>
      </div>
    </div>
  );
}
